import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { ArticleImageFormat } from 'graphql-ts-types';

import type { ProductSuggestionItem } from '../../../../../global-header/components/toolbar/types';
import type { SearchSuggestionsV2Query } from '../../../../gql/generated/schema';
import { useGQLLocale } from '../../../../hooks/locale';
import type { CioFeatureFlag } from '../../../../tracking/cioFeatureFlags';
import { pushCioFeatureFlagsToDataLayer } from '../../../../tracking/cioFeatureFlags';
import isWebpSupported from '../../../../utils/isWebpSupported';
import { searchSuggestionsQuery } from '../../gql/query/suggestions';
import type { Suggestion } from '../../gql/types';
import type { SuggestionItem } from '../../types';
import { attachEncodedUrl, getInputLength } from '../../utils/search';
import { useSuggestionsHandlers } from './handlers';
import type { SuggestionsConfig, UseSuggestionsProps } from './types';

type SuggestionGroup = Suggestion | ProductSuggestionItem;

interface ShouldOpenSuggestionsOptions
  extends
    Pick<UseSuggestionsProps, 'inputValue' | 'isContainerHidden'>,
    Pick<SuggestionsConfig, 'searchThreshold'> {
  suggestions: SuggestionItem[];
}

const isProductSuggestion = (
  suggestion: Suggestion | ProductSuggestionItem
): suggestion is ProductSuggestionItem => 'productImage' in suggestion;

const isKeywordSuggestion = (
  suggestion: Suggestion | ProductSuggestionItem
): suggestion is Suggestion => !isProductSuggestion(suggestion);

interface GetSuggestionsParams {
  suggestionsList: SearchSuggestionsV2Query['suggestions'];
  suggestionsLimit: number;
  productsLimit: number;
  urlParams?: string;
}

const getSuggestions = ({
  suggestionsList,
  suggestionsLimit,
  productsLimit,
  urlParams,
}: GetSuggestionsParams) => {
  const nonEmptySuggestions = suggestionsList.filter(
    // All items contain __typename by default. checking for length > 1 to verify item has actual data
    item => item && Object.keys(item).length > 1
  ) as SuggestionGroup[];

  const suggestions = nonEmptySuggestions
    .filter(isKeywordSuggestion)
    .map(suggestion => attachEncodedUrl(suggestion, urlParams))
    .slice(0, suggestionsLimit);

  const productSuggestions = nonEmptySuggestions
    .filter(isProductSuggestion)
    .slice(0, productsLimit);

  return { suggestions, productSuggestions };
};

const shouldOpenSuggestions =
  ({ inputValue, searchThreshold, suggestions, isContainerHidden }: ShouldOpenSuggestionsOptions) =>
  () =>
    getInputLength(inputValue) >= searchThreshold && suggestions.length > 0 && !isContainerHidden;

const getNames = (items: SuggestionItem[]) => items.map(({ name }) => name);

const trackCioFeatureFlags = (suggestions: SearchSuggestionsV2Query['suggestions']) => {
  const allFlags: CioFeatureFlag[] = [];
  for (const item of suggestions) {
    if (item != null && 'cioFeatureFlags' in item && Array.isArray(item.cioFeatureFlags)) {
      allFlags.push(...item.cioFeatureFlags);
    }
  }

  const uniqueFlags = Array.from(
    new Map(allFlags.map(f => [`${f.featureName}:${f.featureValue}`, f])).values()
  );

  pushCioFeatureFlagsToDataLayer(uniqueFlags);
};

export const useSuggestions = ({
  inputValue,
  setIsBodyOverlayVisible,
  isEditedByUser,
  isInputFocused,
  isMobile,
  config: { searchThreshold, suggestionsLimit, productsLimit, searchDebounce },
  unlockSearchAndClearInput,
  params,
  withProductSuggestions,
  urlParams,
  isContainerHidden,
  setIsContainerHidden,
}: UseSuggestionsProps) => {
  const locale = useGQLLocale();
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [productSuggestions, setProductSuggestions] = useState<ProductSuggestionItem[]>([]);
  const clear = () => {
    setSuggestions([]);
    setProductSuggestions([]);
  };

  const isOpen = useMemo(
    shouldOpenSuggestions({
      inputValue,
      suggestions,
      searchThreshold,
      isContainerHidden,
    }),
    [inputValue, suggestions, searchThreshold, isContainerHidden]
  );
  const listItems = useMemo(() => getNames(suggestions), [suggestions]);
  const handlers = useSuggestionsHandlers({
    suggestions,
    listItems,
    inputValue,
    isMobile,
    clear,
    unlockSearchAndClearInput,
    setIsBodyOverlayVisible,
  });
  const [search] = useLazyQuery(searchSuggestionsQuery);

  useEffect(() => {
    let timeout = null;
    let canceled = false;
    const prefix = inputValue.trim();

    if (prefix.length >= searchThreshold) {
      timeout = setTimeout(
        async () => {
          try {
            const { data } = await search({
              variables: {
                prefix,
                locale,
                urlParams,
                withProductSuggestions,
                ...params,
                ...(isWebpSupported() && { format: ArticleImageFormat.Webp }),
              },
            });
            if (!canceled) {
              const result = getSuggestions({
                suggestionsList: data.suggestions,
                suggestionsLimit,
                productsLimit,
                urlParams,
              });
              if (!isEditedByUser && !isInputFocused) {
                setIsContainerHidden(true);
              }
              setSuggestions(result.suggestions);
              setProductSuggestions(result.productSuggestions);

              trackCioFeatureFlags(data.suggestions);
            }
          } catch {
            /* We are deliberately ignoring errors as there is no UX behaviour for this */
          }
        },
        isEditedByUser ? searchDebounce : 0
      );
    }

    return () => {
      clearTimeout(timeout);
      canceled = true;
    };
  }, [
    inputValue,
    isEditedByUser,
    isInputFocused,
    searchThreshold,
    suggestionsLimit,
    productsLimit,
    searchDebounce,
  ]);

  return {
    suggestions,
    productSuggestions,
    listItems,
    flags: { isOpen },
    handlers,
  };
};
